import { Fragment, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { SEARCH_KIND_LABELS, SEARCH_KINDS, foldSearchText, type GlobalSearchHit } from '@kontor/shared';
import { api, queryString } from '../api';
import { searchSections, type SetupSection } from '../setupSections';
import './globalsearch.css';

export interface SearchPage {
  label: string;
  path: string;
  /** Bereich der Seitenleiste oder „Einrichtung“, steht als Zusatz hinter dem Namen */
  group: string;
}

/** Schnellaktion („Neues Angebot“), gefunden über Bezeichnung und Stichwörter */
export interface SearchAction {
  label: string;
  path: string;
  keywords?: string[];
}

interface Row {
  key: string;
  heading: string;
  title: string;
  detail: string | null;
  path: string;
}

interface Recent {
  title: string;
  detail: string | null;
  path: string;
  heading: string;
}

const RECENT_KEY = 'kontor.search.recent';
const readRecent = (): Recent[] => {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as unknown;
    return Array.isArray(list) ? (list.filter((r): r is Recent => !!r && typeof (r as Recent).path === 'string' && typeof (r as Recent).title === 'string') as Recent[]).slice(0, 6) : [];
  } catch {
    return [];
  }
};
const writeRecent = (list: Recent[]) => {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    // ohne Speicher gibt es keine Liste der zuletzt geöffneten Treffer
  }
};

const isTyping = (target: EventTarget | null) => {
  const el = target as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
};

/** Hebt die Suchwörter im Text hervor, auch wenn der Text Umlaute hat und das Wort nicht („muller“ markiert „Müller“) */
function highlight(text: string, words: string[]): ReactNode {
  if (words.length === 0) return text;
  // Positionen im gefalteten Text sind nur dann dieselben wie im Original, wenn jedes Zeichen genau ein Zeichen bleibt
  const folded = [...text].map((c) => foldSearchText(c) || c);
  if (folded.some((c) => c.length !== 1)) return text;
  const flat = folded.join('');
  const marks = new Array<boolean>(text.length).fill(false);
  for (const w of words) {
    let at = flat.indexOf(w);
    while (at !== -1) {
      for (let i = at; i < at + w.length; i += 1) marks[i] = true;
      at = flat.indexOf(w, at + w.length);
    }
  }
  const out: ReactNode[] = [];
  let i = 0;
  while (i < text.length) {
    let j = i;
    while (j < text.length && marks[j] === marks[i]) j += 1;
    const piece = text.slice(i, j);
    out.push(marks[i] ? <mark key={i}>{piece}</mark> : <Fragment key={i}>{piece}</Fragment>);
    i = j;
  }
  return out;
}

/**
 * Globale Suche in der Kopfleiste: Ein Suchbegriff durchsucht die Seiten des Menüs, die Schnellaktionen („Neues Angebot“), die Bereiche der Einrichtung und, über den Server,
 * alle Daten, die der Benutzer sehen darf. Mehrere Wörter müssen alle passen, Umlaute und Groß- und Kleinschreibung spielen keine Rolle, ein Tippfehler am Wortende wird
 * verziehen. Ohne Eingabe zeigt das Feld die zuletzt geöffneten Treffer und Schnellaktionen. Strg+K oder „/“ springt ins Feld, Pfeiltasten und Enter wählen, Escape schließt.
 * Auf dem Handy steht nur eine Lupe, die das Suchfeld über die Kopfleiste legt.
 */
export function GlobalSearch({ pages, sections, actions }: { pages: SearchPage[]; sections: SetupSection[]; actions: SearchAction[] }) {
  const uid = useId();
  const navigate = useNavigate();
  const root = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [text, setText] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [active, setActive] = useState(0);
  const [recent, setRecent] = useState<Recent[]>(readRecent);

  // Erst nach einer kurzen Pause beim Server suchen
  useEffect(() => {
    const t = setTimeout(() => setDebounced(text.trim()), 250);
    return () => clearTimeout(t);
  }, [text]);

  const found = useQuery({
    queryKey: ['global-search', debounced],
    queryFn: () => api<{ data: GlobalSearchHit[]; meta: { fuzzy: boolean } }>('GET', `/search${queryString({ q: debounced })}`),
    enabled: debounced.length >= 2,
    staleTime: 15_000,
  });

  const query = text.trim();
  const words = useMemo(() => foldSearchText(query).split(' ').filter(Boolean), [query]);
  const fuzzy = !!found.data?.meta.fuzzy && debounced === query;

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    if (query.length < 2) {
      for (const r of recent) out.push({ key: `recent-${r.path}`, heading: 'Zuletzt geöffnet', title: r.title, detail: r.detail ? `${r.heading} · ${r.detail}` : r.heading, path: r.path });
      for (const a of actions.slice(0, 6)) out.push({ key: `action-${a.path}`, heading: 'Neu anlegen', title: a.label, detail: null, path: a.path });
      return out;
    }
    const wordsMatch = (hay: string) => words.every((w) => foldSearchText(hay).includes(w));
    for (const a of actions.filter((a) => wordsMatch(`${a.label} ${(a.keywords ?? []).join(' ')}`)).slice(0, 4)) out.push({ key: `action-${a.path}`, heading: 'Neu anlegen', title: a.label, detail: null, path: a.path });
    for (const p of pages.filter((p) => wordsMatch(`${p.label} ${p.group}`)).slice(0, 5)) out.push({ key: `page-${p.path}`, heading: 'Seiten', title: p.label, detail: p.group, path: p.path });
    for (const s of searchSections(sections, query).slice(0, 4)) out.push({ key: `setup-${s.path}`, heading: 'Einrichtung', title: s.label, detail: s.description, path: `/einrichtung/${s.path}` });
    if (found.data && debounced === query) {
      for (const kind of SEARCH_KINDS) for (const h of found.data.data.filter((x) => x.kind === kind)) out.push({ key: `${kind}-${h.id}`, heading: SEARCH_KIND_LABELS[kind], title: h.title, detail: h.detail, path: h.path });
    }
    return out;
  }, [query, words, debounced, found.data, pages, sections, actions, recent]);

  useEffect(() => setActive(0), [rows.length, query]);

  const close = () => {
    setOpen(false);
    setMobile(false);
  };
  const go = (row: Row) => {
    close();
    setText('');
    // Treffer aus den Daten und Seiten merken, Schnellaktionen nicht
    if (!row.key.startsWith('action-') && !row.key.startsWith('recent-')) {
      const next = [{ title: row.title, detail: row.detail, path: row.path, heading: row.heading }, ...recent.filter((r) => r.path !== row.path)].slice(0, 6);
      setRecent(next);
      writeRecent(next);
    }
    navigate(row.path);
  };
  const focusInput = () => {
    setMobile(true);
    setOpen(true);
    setTimeout(() => input.current?.focus(), 0);
  };

  // Tastenkürzel: Strg+K (oder Cmd+K) und „/“ öffnen die Suche, Escape und ein Klick daneben schließen sie
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') || (e.key === '/' && !isTyping(e.target) && !e.ctrlKey && !e.metaKey)) {
        e.preventDefault();
        focusInput();
      } else if (e.key === 'Escape') close();
    };
    const onPointer = (e: PointerEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, []);

  const showPanel = open && (query.length >= 2 || rows.length > 0);
  const loading = found.isFetching && debounced === query;
  const noHits = query.length >= 2 && !loading && rows.length === 0;
  let lastHeading = '';

  return (
    <div className={`gsearch${mobile ? ' is-mobile-open' : ''}`} ref={root}>
      <button type="button" className="topbar__icon gsearch__toggle" aria-label="Suchen" title="Suchen (Strg+K)" onClick={focusInput}>
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
      </button>
      <div className="gsearch__box" role="search">
        <svg className="gsearch__icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
        <input
          ref={input}
          className="gsearch__input"
          type="search"
          role="combobox"
          aria-label="Suche in Wirkia"
          aria-expanded={showPanel}
          aria-controls={`${uid}-list`}
          aria-activedescendant={showPanel && rows[active] ? `${uid}-${active}` : undefined}
          autoComplete="off"
          placeholder="Alles durchsuchen …  Strg+K"
          value={text}
          onChange={(e) => { setText(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((a) => Math.min(a + 1, Math.max(rows.length - 1, 0)));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === 'Enter' && rows[active]) {
              e.preventDefault();
              go(rows[active]!);
            }
          }}
        />
        {mobile && <button type="button" className="gsearch__close" aria-label="Suche schließen" onClick={() => { close(); setText(''); }}>×</button>}
        {showPanel && (
          <div className="gsearch__panel" id={`${uid}-list`} role="listbox" aria-label="Suchergebnisse">
            {fuzzy && <p className="gsearch__note">Keine genauen Treffer. Ähnliche Ergebnisse für „{query}“:</p>}
            {rows.map((r, i) => {
              const heading = r.heading !== lastHeading ? r.heading : null;
              lastHeading = r.heading;
              return (
                <div key={r.key}>
                  {heading && <p className="gsearch__heading">{heading}</p>}
                  <div id={`${uid}-${i}`} role="option" aria-selected={i === active} className={`gsearch__row${i === active ? ' is-active' : ''}`} onMouseEnter={() => setActive(i)} onMouseDown={(e) => { e.preventDefault(); go(r); }}>
                    <span className="gsearch__title">{query.length >= 2 ? highlight(r.title, words) : r.title}</span>
                    {r.detail && <span className="gsearch__detail">{query.length >= 2 ? highlight(r.detail, words) : r.detail}</span>}
                  </div>
                </div>
              );
            })}
            {loading && <p className="gsearch__note">{rows.length > 0 ? 'Weitere Treffer werden gesucht …' : 'Suche läuft …'}</p>}
            {noHits && (
              <div className="gsearch__note">
                <p>Nichts gefunden für „{query}“.</p>
                <p>Tipps: Suchen Sie nach Namen, Nummern (RE-2026-0001), Kennzeichen oder Orten. Mehrere Wörter grenzen ein („mercedes bus“), Umlaute dürfen fehlen („muller“).</p>
              </div>
            )}
            {found.isError && <p className="gsearch__note">Die Suche in den Daten ist gerade nicht möglich.</p>}
            <p className="gsearch__keys" aria-hidden="true">↑↓ wählen · Enter öffnen · Esc schließen</p>
          </div>
        )}
      </div>
    </div>
  );
}
